import type {
  FormElement,
  FormElementBase,
  FormElementEditor,
  FormElementOption,
  FormElementRadio,
  FormElementValue,
  FormMode,
} from 'src/library/components/form';
import type { FunctionViewModel } from '@trinnvis/dabih-api-client';
import { functionsNotDeleted } from 'src/store/selectors/responsibilities/functions-not-deleted';
import { currentEmployeeUuid } from 'src/store/selectors/employees/current-employee-uuid';
import { LocalDate } from 'src/utilities/local-date';
import { relativeDaysAsText } from 'src/utilities/text';
import { eventAssigneesText } from 'src/store/eventAssigneesText';
import { employeesShortNamesAndMe } from 'src/store/selectors/employees/employees-short-names-and-me';
import { getContactsAsUsers } from 'src/store/selectors/partners/get-contacts-as-users';
import type { State } from 'src/store/types';
import type { FormUserValue, SectionFieldData } from 'src/library/components/section-field';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import type { BarGraphItem } from 'src/library/elements/bar-graph-item';
import { t } from 'src/i18n';

export function isEditor(object: FormElementBase): object is FormElementEditor {
  return (
    object.type === 'text' ||
    object.type === 'textarea' ||
    object.type === 'date' ||
    object.type === 'check' ||
    object.type === 'radio'
  );
}

export function isRadio(object: FormElementBase): object is FormElementRadio {
  return object.type === 'radio';
}

export function extractFormData(
  elements: FormElement[],
  uuid: string = '',
): {
  respondentFieldsCount: number;
  respondentMultipleFieldsCount: number;
  responsibleFieldsCount: number;
  responsibleResponseCount: number;
  respondentResponseCount: number;
  currentUserResponseCount: number;
} {
  let respondentFieldsCount = 0;
  let respondentMultipleFieldsCount = 0;
  let responsibleFieldsCount = 0;
  let responsibleResponseCount = 0;
  let respondentResponseCount = 0;
  let currentUserResponseCount = 0;
  elements.forEach((e) => {
    if (isEditor(e)) {
      if (e.editors !== 'responsibles') {
        respondentFieldsCount++;
        if ('values' in e && e.values && e.values.find((v) => v.value !== '') !== undefined) {
          respondentResponseCount += e.values.length;
          const response = e.values.find((v) => {
            return v.userUuid === uuid;
          });
          if (response) {
            currentUserResponseCount++;
          }
        }
        if (e.responseType === 'multiple') {
          respondentMultipleFieldsCount++;
        }
      }
      if (e.editors !== 'respondents') {
        responsibleFieldsCount++;
        if ('values' in e && e.values && e.values.find((v) => v.value !== '') !== undefined) {
          responsibleResponseCount++;
        }
      }
    }
    if ('elements' in e) {
      respondentFieldsCount += extractFormData(e.elements ?? [], uuid).respondentFieldsCount;
      respondentMultipleFieldsCount += extractFormData(e.elements ?? [], uuid).respondentMultipleFieldsCount;
      responsibleFieldsCount += extractFormData(e.elements ?? [], uuid).responsibleFieldsCount;
      responsibleResponseCount += extractFormData(e.elements ?? [], uuid).responsibleResponseCount;
      respondentResponseCount += extractFormData(e.elements ?? [], uuid).respondentResponseCount;
      currentUserResponseCount += extractFormData(e.elements ?? [], uuid).currentUserResponseCount;
    }
  });
  return {
    respondentFieldsCount,
    respondentMultipleFieldsCount,
    responsibleFieldsCount,
    responsibleResponseCount,
    respondentResponseCount,
    currentUserResponseCount,
  };
}

export function formResponseCount(elements: FormElement[]): number {
  let count = 0;
  elements.forEach((e) => {
    if ('values' in e && e.values && e.values.length) {
      count = e.values.length;
    } else if ('elements' in e && e.elements && e.elements.length) {
      return formResponseCount(e.elements);
    }
  });
  return count;
}

function isFormResponsibleCurrentEmployee(
  responsibleFunction: string,
  responsibleEmployees: string[],
  functions: FunctionViewModel[],
  currentEmployeeUuid: string,
) {
  const func = functions.find((f) => {
    return f.uuid === responsibleFunction;
  });
  if (func) {
    responsibleEmployees = responsibleEmployees.concat(func.employees);
  }
  return responsibleEmployees.includes(currentEmployeeUuid);
}

export function getFormPrompt(
  state: State,
  functionUuid: string,
  employees: string[],
  contacts: string[],
  deadline: string,
) {
  const formResponsible = eventAssigneesText(
    {
      functionUuid,
      employees,
      contacts,
    },
    functionsNotDeleted(state),
    employeesShortNamesAndMe(state),
    getContactsAsUsers(state),
  );
  let prompt = t('forms.prompt.fillOutByResponsible', { name: formResponsible });
  if (
    isFormResponsibleCurrentEmployee(
      functionUuid,
      employees,
      functionsNotDeleted(state),
      currentEmployeeUuid(state) ?? '',
    )
  ) {
    prompt = t('forms.prompt.fillOut');
  }
  if (deadline) {
    prompt += t('forms.prompt.deadlineSuffix', {
      date: LocalDate.fromString(deadline).toStringForDisplay(),
      relativeDays: relativeDaysAsText(deadline, LocalDate.fromString()),
    });
  }
  return prompt;
}

function isSurvey(formContent: FormElement[]): boolean {
  return extractFormData(formContent).respondentMultipleFieldsCount > 0;
}

export function hasColoredOptions(options: FormElementOption[]): boolean {
  return options.find((o) => o.color !== '' && o.color !== '#ffffff') !== undefined;
}

function optionColor(options: FormElementOption[], value: string): string {
  const option = options.find((o) => o.value === value);
  if (option) {
    if (option.color !== '' && option.color !== '#ffffff') {
      return option.color;
    }
  }
  return 'var(--themeColor)';
}

function formatValue(type: string, value: string): string {
  if (type === 'date' && value !== '') {
    return LocalDate.fromString(value).toStringForDisplay();
  }
  return value;
}

function userName(userNames: SelectDropdownOption[], uuid: string): string {
  const user = userNames.find((user) => {
    return user.value === uuid;
  });
  if (user) {
    return user.text;
  }
  return '';
}

export function getAllValues(
  userNames: SelectDropdownOption[],
  type: string,
  options: FormElementOption[] | undefined,
  values: FormElementValue[],
): FormUserValue[] {
  if (values.length) {
    const result: FormUserValue[] = [];
    values.forEach((value) => {
      if (value.value) {
        const item: FormUserValue = {
          value: formatValue(type, value.value),
          userName: userName(userNames, value.userUuid),
        };
        if (options && hasColoredOptions(options)) {
          item.optionColor = optionColor(options, value.value);
        }
        result.push(item);
      }
    });
    return result;
  }
  return [];
}

export function getUserValue(currentUserUuid: string, element: FormElementEditor, format: boolean = false): string {
  const userValue = element.values.find((value) => value.userUuid === currentUserUuid);
  if (userValue) {
    if (format) {
      return formatValue(element.type, userValue.value);
    }
    return userValue.value;
  }
  return '';
}

function barGraphItems(respondents: SelectDropdownOption[], element: FormElementRadio): BarGraphItem[] {
  const items: BarGraphItem[] = element.options.map((o) => {
    const count = element.values.filter((v) => {
      return v.value === o.value;
    }).length;
    return { count, color: o.color, label: o.value };
  });
  const answered = element.values.filter((v) => v.value).length;
  const unanswered = respondents.length - answered;
  items.push({ count: unanswered, color: '#E6E6E6', label: t('forms.notAnswered') });
  return items;
}

function getFormElementsWithValuesForView(
  isSurvey: boolean,
  mode: FormMode,
  userNames: SelectDropdownOption[],
  currentUserUuid: string,
  respondents: SelectDropdownOption[],
  anonymous: boolean,
  elements: FormElement[] = [],
  prevElementValue: string = '',
): SectionFieldData[] {
  const result: SectionFieldData[] = [];
  elements.forEach((element: FormElement) => {
    let singularValue = '';
    let userValues: FormUserValue[] = [];
    if (isEditor(element)) {
      if (element.values && element.values.length) {
        if (!isSurvey || mode === 'viewAsResponsible' || mode === 'editAsResponsible') {
          let options: FormElementOption[] | undefined = undefined;
          if (isRadio(element)) {
            options = element.options;
          }
          userValues = getAllValues(userNames, element.type, options, element.values);
        } else {
          singularValue = getUserValue(currentUserUuid, element, true);
        }
      }
      if (mode === 'viewAsResponsible' || mode === 'editAsResponsible' || element.editors !== 'responsibles') {
        const item: SectionFieldData = {
          field: element.path ?? '',
          type: 'view-html',
          label: element.label ?? '',
        };
        if (singularValue) {
          item.value = singularValue;
        } else if (userValues && !anonymous) {
          item.userValues = userValues;
        }
        if (
          element.type === 'radio' &&
          element.responseType === 'multiple' &&
          respondents.length > 0 &&
          (mode === 'viewAsResponsible' || mode === 'editAsResponsible')
        ) {
          item.type = 'view-html-with-graph';
          item.barGraphItems = barGraphItems(respondents, element);
        }
        result.push(item);
      }
    } else if (element.type === 'conditionalSection') {
      getFormElementsWithValuesForView(
        isSurvey,
        mode,
        userNames,
        currentUserUuid,
        respondents,
        anonymous,
        element.elements,
        prevElementValue,
      ).forEach((e) => {
        if (!isSurvey || mode === 'viewAsResponsible' || element.condition.includes(prevElementValue)) {
          result.push(e);
        }
      });
    }
    if (isEditor(element)) prevElementValue = getUserValue(currentUserUuid, element, true);
  });
  return result;
}

export function getFormContentWithValuesForView(
  formContent: FormElement[],
  mode: FormMode,
  userNames: SelectDropdownOption[],
  currentUserUuid: string,
  respondents: SelectDropdownOption[],
  anonymous: boolean,
): SectionFieldData[] {
  const result: SectionFieldData[] = [];
  formContent.forEach((element: FormElement) => {
    result.push({
      field: element.path ?? '',
      type: 'header',
      label: 'label' in element && element.label ? element.label : '',
      description: element.description ?? '',
    });
    if (element.type === 'section' || element.type === 'conditionalSection') {
      getFormElementsWithValuesForView(
        isSurvey(formContent),
        mode,
        userNames,
        currentUserUuid,
        respondents,
        anonymous,
        element.elements,
      ).forEach((field) => {
        result.push(field);
      });
    }
  });
  return result;
}
